import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SiteMedia, MediaLimits } from '../../../types/site';
import { siteService } from '../../../services/siteService';
import { MEDIA_ENCODING } from '../../../config/mediaEncoding';
import { semaphore } from '../../../utils/semaphore';
import {
  processFileForUpload,
  validateSourceFile,
  SUPPORTED_IMAGE_TYPES,
  SUPPORTED_VIDEO_TYPES,
} from '../../../utils/mediaConverter';

/**
 * Per-site media, addressed by SLOT.
 *
 * Images: slot 0 is the thumbnail — a smaller rendition for the site card,
 * uploaded by the admin rather than generated — and slots 1..N-1 are full-size
 * photos. Videos occupy slots 1..N.
 *
 * Uploading into an occupied slot REPLACES it. The server upserts on
 * (store, site, type, slot), so a duplicate slot can never create a second row.
 */

interface SiteMediaFormProps {
  siteAlias: string;
  media: SiteMedia[];
  onChange: (media: SiteMedia[]) => void;
}

type MediaKind = 'image' | 'video';
type SlotStatus = 'idle' | 'validating' | 'converting' | 'uploading' | 'error';

interface SlotState {
  status: SlotStatus;
  message?: string;
  /** 0..1 while ffmpeg runs; videos take long enough that this is not optional. */
  progress?: number;
}

const THUMB_SLOT = 0;

/**
 * How long a fetched limits response stays usable.
 *
 * Limits are re-checked before each batch so a cap tightened mid-session cannot
 * reject an upload only after the admin waited through conversion. Without a TTL
 * that means one request per file-picker interaction, so filling eight slots one
 * at a time would make eight near-identical calls.
 *
 * Three minutes collapses a normal editing session into one or two requests
 * while keeping the staleness window bounded.
 */
const LIMITS_TTL_MS = 180_000;

const SiteMediaForm: React.FC<SiteMediaFormProps> = ({ siteAlias, media, onChange }) => {
  const [limits, setLimits] = useState<MediaLimits | null>(null);
  const [limitsError, setLimitsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [slotState, setSlotState] = useState<Record<string, SlotState>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // One hidden input per slot, so "upload into slot 5" is unambiguous.
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const limitsCache = useRef<{ value: MediaLimits; fetchedAt: number } | null>(null);

  const key = (kind: MediaKind, slot: number) => `${kind}:${slot}`;

  // ─── Limits ───

  const loadLimits = useCallback(async (): Promise<MediaLimits | null> => {
    const cached = limitsCache.current;
    if (cached && Date.now() - cached.fetchedAt < LIMITS_TTL_MS) {
      return cached.value;
    }
    try {
      const fetched = await siteService.getMediaLimits();
      limitsCache.current = { value: fetched, fetchedAt: Date.now() };
      setLimits(fetched);
      setLimitsError(null);
      return fetched;
    } catch (e: any) {
      // Deliberately no local fallback defaults: a second copy of these numbers
      // that disagrees with the server produces 400s the admin cannot interpret.
      setLimitsError(e?.message || 'Could not load media limits.');
      return null;
    }
  }, []);

  useEffect(() => {
    loadLimits();
  }, [loadLimits]);

  // ─── Existing media for this site ───

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      // Per-site fetch, not the whole store: a site holds up to 11 rows, so a
      // large store would be silently truncated by the endpoint's limit.
      const rows = await siteService.getSiteMedia(siteAlias);
      onChange(rows);
      setError(null);
    } catch (e: any) {
      setError(`Failed to load media: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [siteAlias, onChange]);

  useEffect(() => {
    loadMedia();
    setSlotState({});
    setDrafts({});
  }, [loadMedia]);

  // Warn before the tab is closed/reloaded while conversions or uploads are in
  // flight — it all runs client-side, so leaving cancels it and loses the files.
  useEffect(() => {
    if (!busy) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  const findMedia = (kind: MediaKind, slot: number): SiteMedia | undefined =>
    media.find((m) => m.media_type === kind && m.display_order === slot);

  const slotsFor = (kind: MediaKind): number[] => {
    if (!limits) return [];
    // photos.max_count INCLUDES the thumbnail, so 8 → slots 0..7.
    return kind === 'image'
      ? Array.from({ length: limits.photos.max_count }, (_, i) => i)
      : Array.from({ length: limits.videos.max_count }, (_, i) => i + 1);
  };

  const setSlot = (kind: MediaKind, slot: number, patch: Partial<SlotState>) =>
    setSlotState((prev) => ({
      ...prev,
      [key(kind, slot)]: { ...prev[key(kind, slot)], ...patch } as SlotState,
    }));

  // ─── Upload ───

  /**
   * Convert one file and upload it into a specific slot.
   *
   * Slot 0 converts to the thumbnail dimension rather than full size — otherwise
   * the "thumbnail" is a full-size image and the card page gains nothing, which
   * is the entire reason the slot exists.
   */
  const uploadOne = async (
    file: File,
    kind: MediaKind,
    slot: number,
    activeLimits: MediaLimits,
    convertSem: ReturnType<typeof semaphore>,
    uploadSem: ReturnType<typeof semaphore>,
  ) => {
    setSlot(kind, slot, { status: 'validating', message: undefined, progress: undefined });

    // Validate the SOURCE before converting — encoding costs seconds for images
    // and minutes for video, so a rejection afterwards is one the admin paid for.
    const sourceCheck = await validateSourceFile(file, kind, {
      maxImageBytes: MEDIA_ENCODING.source_max_image_bytes,
      maxVideoBytes: MEDIA_ENCODING.source_max_video_bytes,
      maxImagePixels: MEDIA_ENCODING.max_image_pixels,
      maxImageEdgePx: MEDIA_ENCODING.max_image_edge_px,
      maxVideoSeconds: activeLimits.videos.max_duration_seconds,
    });
    if (!sourceCheck.valid) {
      setSlot(kind, slot, { status: 'error', message: sourceCheck.error ?? 'Invalid file' });
      return;
    }

    let blob: Blob;
    let filename: string;
    try {
      setSlot(kind, slot, { status: 'converting', progress: 0 });
      const processed = await convertSem(() =>
        processFileForUpload(file, {
          imageQuality: MEDIA_ENCODING.image_quality,
          imageMaxWidth: slot === THUMB_SLOT ? MEDIA_ENCODING.thumb_max_px : MEDIA_ENCODING.image_max_px,
          imageMaxHeight: slot === THUMB_SLOT ? MEDIA_ENCODING.thumb_max_px : MEDIA_ENCODING.image_max_px,
          videoMaxWidth: MEDIA_ENCODING.video_max_width,
          videoCrf: MEDIA_ENCODING.video_crf,
          videoPreset: MEDIA_ENCODING.video_preset,
          videoFpsCap: MEDIA_ENCODING.video_fps_cap,
          onProgress: ({ ratio }) => setSlot(kind, slot, { progress: ratio }),
        }),
      );
      blob = processed.blob;
      filename = processed.fileName;
    } catch (e: any) {
      setSlot(kind, slot, { status: 'error', message: `Conversion failed: ${e?.message || e}` });
      return;
    }

    // Check the CONVERTED size, not a predicted one. Output tracks content and
    // duration rather than input size, so any assumed compression ratio is wrong
    // in both directions.
    const maxBytes = kind === 'image' ? activeLimits.photos.max_bytes : activeLimits.videos.max_bytes;
    if (blob.size > maxBytes) {
      const mb = (n: number) => (n / (1024 * 1024)).toFixed(1);
      setSlot(kind, slot, {
        status: 'error',
        message:
          `Converted to ${mb(blob.size)}MB, over the ${mb(maxBytes)}MB limit. ` +
          (kind === 'video'
            ? 'Try a shorter clip, or film steadier and in better light.'
            : 'Try a smaller image.'),
      });
      return;
    }

    try {
      setSlot(kind, slot, { status: 'uploading', progress: undefined });
      const description = drafts[key(kind, slot)]?.trim() || undefined;
      await uploadSem(() =>
        siteService.uploadSiteMedia(blob, filename, siteAlias, kind, slot, description),
      );
      setSlot(kind, slot, { status: 'idle', message: undefined });
    } catch (e: any) {
      setSlot(kind, slot, { status: 'error', message: `Upload failed: ${e?.message || e}` });
    }
  };

  /**
   * Run a batch: conversions serial, uploads limited, pipelined between the two.
   * Failures are isolated per file — one bad file must not discard the others.
   */
  const runBatch = async (items: Array<{ file: File; kind: MediaKind; slot: number }>) => {
    const activeLimits = await loadLimits();
    if (!activeLimits) {
      setError('Could not load media limits. Try again.');
      return;
    }

    // Conversion is CPU-bound, so parallel encodes just split the same cores at
    // N× the memory; uploads are network-bound and do benefit from overlap.
    const convertSem = semaphore(1);
    const uploadSem = semaphore(Math.max(1, MEDIA_ENCODING.upload_concurrency));

    setBusy(true);
    setError(null);
    try {
      await Promise.allSettled(
        items.map((it) => uploadOne(it.file, it.kind, it.slot, activeLimits, convertSem, uploadSem)),
      );
      await loadMedia();
    } finally {
      setBusy(false);
    }
  };

  /** Upload into one specific slot — the "replace this photo" action. */
  const handleSlotPick =
    (kind: MediaKind, slot: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      runBatch([{ file, kind, slot }]);
    };

  /**
   * Multi-select: assign files to the LOWEST EMPTY slots, in selection order.
   * Reject the surplus up front rather than silently dropping files or
   * overwriting existing ones — replacing is always a deliberate single action.
   */
  const handleBulkPick = (kind: MediaKind) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!picked.length) return;

    const empty = slotsFor(kind).filter((s) => !findMedia(kind, s));
    if (picked.length > empty.length) {
      setError(
        `Selected ${picked.length} file(s) but only ${empty.length} empty ` +
          `${kind === 'image' ? 'photo' : 'video'} slot(s) are available. ` +
          `Remove some, or replace individual slots instead.`,
      );
      return;
    }
    setError(null);
    runBatch(picked.map((file, i) => ({ file, kind, slot: empty[i] })));
  };

  const handleDelete = async (m: SiteMedia) => {
    const label = m.display_order === THUMB_SLOT ? 'the thumbnail' : `slot ${m.display_order}`;
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await siteService.deleteSiteMedia(m.id);
      await loadMedia();
    } catch (e: any) {
      setError(`Failed to delete: ${e.message}`);
    }
  };

  // ─── Render ───

  const renderSlot = (kind: MediaKind, slot: number) => {
    const existing = findMedia(kind, slot);
    const k = key(kind, slot);
    const state = slotState[k];
    const isThumb = kind === 'image' && slot === THUMB_SLOT;
    const label = isThumb ? 'Thumbnail' : `${kind === 'image' ? 'Photo' : 'Video'} ${slot}`;
    const inFlight = !!state && state.status !== 'idle' && state.status !== 'error';

    return (
      <div key={k} className={`media-slot ${existing ? 'filled' : 'empty'} ${isThumb ? 'thumb' : ''}`}>
        <div className="slot-label">
          {label}
          {isThumb && (
            <span
              className="slot-hint"
              title="Shown on the site card — upload a smaller, tightly cropped image"
            >
              card image
            </span>
          )}
        </div>

        <div className="slot-preview">
          {existing && kind === 'image' && (
            // alt comes from the description — without it a screen reader
            // announces a filename, which tells the user nothing.
            <img src={existing.url} alt={existing.description || label} />
          )}
          {existing && kind === 'video' && <video src={existing.url} controls preload="metadata" />}
          {!existing && !inFlight && <div className="slot-placeholder">Empty</div>}
          {inFlight && (
            <div className="slot-progress">
              <div className="slot-status">{state?.status}…</div>
              {state?.progress !== undefined && <progress value={state.progress} max={1} />}
            </div>
          )}
        </div>

        {existing && (
          <input
            className="slot-description"
            type="text"
            maxLength={limits?.description_max_length ?? 125}
            placeholder="Describe this for guests (also used as alt text)"
            value={drafts[k] ?? existing.description ?? ''}
            onChange={(ev) => setDrafts((d) => ({ ...d, [k]: ev.target.value }))}
            disabled={busy}
          />
        )}

        {state?.status === 'error' && <div className="slot-error">{state.message}</div>}

        <div className="slot-actions">
          <input
            ref={(el) => {
              fileInputs.current[k] = el;
            }}
            type="file"
            accept={(kind === 'image' ? SUPPORTED_IMAGE_TYPES : SUPPORTED_VIDEO_TYPES).join(',')}
            style={{ display: 'none' }}
            onChange={handleSlotPick(kind, slot)}
          />
          <button type="button" onClick={() => fileInputs.current[k]?.click()} disabled={busy}>
            {existing ? 'Replace' : 'Upload'}
          </button>
          {existing && (
            <button
              type="button"
              className="danger"
              onClick={() => handleDelete(existing)}
              disabled={busy}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderGroup = (kind: MediaKind, title: string) => {
    const slots = slotsFor(kind);
    const filled = slots.filter((s) => findMedia(kind, s)).length;
    return (
      <section className="slot-group">
        <div className="slot-group-header">
          <h4>{title}</h4>
          <span className="slot-count">
            {filled} / {slots.length} used
          </span>
          <label className="bulk-pick">
            <input
              type="file"
              multiple
              accept={(kind === 'image' ? SUPPORTED_IMAGE_TYPES : SUPPORTED_VIDEO_TYPES).join(',')}
              style={{ display: 'none' }}
              onChange={handleBulkPick(kind)}
              disabled={busy}
            />
            <span className="link-btn">Add several…</span>
          </label>
        </div>
        <div className="slot-grid">{slots.map((s) => renderSlot(kind, s))}</div>
      </section>
    );
  };

  if (limitsError) {
    return (
      <div className="site-media-form">
        <div className="form-error">
          {limitsError}
          <button type="button" onClick={() => loadLimits()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="site-media-form site-media-slots">
      <div className="form-section-header">
        <h3>Site Media</h3>
        <p className="form-section-description">
          Photos and videos shown to guests during booking. The thumbnail is the site's
          card image — seasonal sites: uploading a thumbnail makes this site's photos
          visible on the booking page.
        </p>
      </div>

      {error && (
        <div className="form-error">
          {error}
          <button type="button" className="error-dismiss" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      {loading && media.length === 0 && <div className="form-loading">Loading media…</div>}

      {limits && (
        <>
          {renderGroup('image', 'Photos')}
          {renderGroup('video', 'Videos')}
          <p className="slot-footnote">
            Files convert in the browser before upload (images → AVIF/WebP, videos → MP4).
            Conversions run one at a time; uploads overlap. Videos can take a minute or
            more — progress is shown per slot.
          </p>
          {busy && (
            <p className="slot-footnote slot-warning">
              ⚠️ Keep this window open — closing it will cancel the upload.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default SiteMediaForm;
